import { useCallback, useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { Link, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { Archive, Ban, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { cn } from '@/lib/utils';

// =====================================================================
// Inbox — "/app/b/:siteId/pulse/inbox" (WP1 Task 10). Moved from the old
// flat `sites/:id/forms` (site-forms.tsx, now retired — the legacy path
// redirects here via route-map.ts's legacyRedirect). Same contact-form
// submission inbox, minus the `<DashboardShell>` wrapper and "Back to
// site" breadcrumb the layer-shell (PanelShell) now supplies. The raw
// message stream this reads — GET /api/forms?siteId=&status= — is the
// SAME store `pulse/leads.tsx` reads a structured view over (spec §7:
// "two views, one underlying store").
// =====================================================================

type Status = 'new' | 'read' | 'archived' | 'spam';

interface Submission {
  id: string;
  dataJson: { name: string; email: string; message: string };
  status: Status;
  submittedAt: string | number;
}

const TABS: { key: Status; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'read', label: 'Read' },
  { key: 'archived', label: 'Archived' },
  { key: 'spam', label: 'Spam' },
];

const STATUS_BADGE: Record<Status, { variant: 'warning' | 'muted' | 'outline' | 'destructive'; label: string }> = {
  new: { variant: 'warning', label: 'New' },
  read: { variant: 'muted', label: 'Read' },
  archived: { variant: 'outline', label: 'Archived' },
  spam: { variant: 'destructive', label: 'Spam' },
};

// One real empty state per tab (design audit T10/A12). "Nothing here" told an
// owner nothing four times over; each tab now names what WOULD be here, why it
// isn't, and gives one thing to press. The New tab's action points at the
// website — an inbox with no messages is usually a contact page nobody has
// found yet — and the other three point back at New, the only tab with work in
// it.
const EMPTY_COPY: Record<Status, { title: string; description: string }> = {
  new: {
    title: "You're all caught up",
    description:
      "Messages from your site's contact form land here first — and we email you the moment one arrives, so you never have to sit on this screen waiting.",
  },
  read: {
    title: 'Nothing read yet',
    description:
      'Open a message and it moves here. That keeps New down to what still needs you, and nothing is ever lost on the way.',
  },
  archived: {
    title: 'Nothing archived yet',
    description:
      "Archive a message once you've replied and it moves out of New into here — still yours to read whenever you want it back.",
  },
  spam: {
    title: 'No spam',
    description:
      'Mark a junk message as spam and it comes here, out of your way. Nothing is deleted unless you delete it yourself.',
  },
};

function relativeTime(value: string | number): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function Inbox() {
  const meState = useMe();
  const { businessId = '', locationId: siteId = '' } = useTenantParams();
  const [tab, setTab] = useState<Status>('new');
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [unread, setUnread] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The old `/api/sites/:id` fetch here existed only to put the business
  // name in the <h1> — the h1 is now the PAGE name (design audit B1), and
  // the business name lives in the sidebar switcher, so the request is gone.

  const loadUnread = useCallback(() => {
    if (!siteId) return;
    void api
      .get<{ count: number }>(locationApi(businessId, siteId, 'forms/unread-count'))
      .then((res) => setUnread(res.count))
      .catch(() => setUnread(null));
  }, [businessId, siteId]);

  const loadList = useCallback(() => {
    if (!siteId) return;
    setSubmissions(null);
    void api
      .get<{ submissions: Submission[] }>(locationApi(businessId, siteId, `forms?status=${tab}`))
      .then((res) => setSubmissions(res.submissions))
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not load messages.');
        setSubmissions([]);
      });
  }, [businessId, siteId, tab]);

  useEffect(() => loadList(), [loadList]);
  useEffect(() => loadUnread(), [loadUnread]);

  // Opening a new message marks it read in place (no list refetch, so the row
  // stays put while the owner reads it); the unread badge refreshes.
  function toggleExpand(s: Submission) {
    const opening = expanded !== s.id;
    setExpanded(opening ? s.id : null);
    if (opening && s.status === 'new') {
      void api
        .patch(locationApi(businessId, siteId, `forms/${s.id}`), { status: 'read' })
        .then(() => {
          setSubmissions((cur) =>
            cur ? cur.map((x) => (x.id === s.id ? { ...x, status: 'read' } : x)) : cur,
          );
          loadUnread();
        })
        .catch(() => undefined);
    }
  }

  function setStatus(s: Submission, status: Status) {
    void api
      .patch(locationApi(businessId, siteId, `forms/${s.id}`), { status })
      .then(() => {
        loadList();
        loadUnread();
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not update.'));
  }

  function remove(s: Submission) {
    if (!window.confirm(`Delete the message from ${s.dataJson.name}? This can't be undone.`)) return;
    void api
      .delete(locationApi(businessId, siteId, `forms/${s.id}`))
      .then(() => {
        loadList();
        loadUnread();
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete.'));
  }

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Pulse"
        title="Inbox"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') return null;

  return (
    <PageContainer>
      {/* No "No unread" pill (design audit T10/A12): the New tab below carries
          the same number, and a pill that reads "No unread" directly above a
          tab strip that also says "New (0)" is the count twice over. */}
      <PageHeader eyebrow="Pulse" title="Inbox" />

      {error ? (
        <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</div>
      ) : null}

      {/* B5: these were "a quiet segmented control" in spirit only — four
          bare buttons with an underline, no group semantics and no keyboard
          contract. They are a single choice over four options, which is what
          SegmentedControl already is. */}
      <SegmentedControl<Status>
        ariaLabel="Filter messages"
        value={tab}
        options={TABS.map((t) => {
          // Only ever a count we actually hold. New reads the dedicated
          // unread endpoint, ALWAYS — including while New is the open tab
          // (fix round 1): opening a message marks it read in place and
          // deliberately leaves the row where it is, so the list length keeps
          // counting a message that is no longer new, while `loadUnread()`
          // has already refreshed the true number. The other three count the
          // list on screen while they are open, and read `null` before that —
          // one `/api/forms` call returns one status, and inventing the rest
          // would put a wrong number in front of the owner.
          const count =
            t.key === 'new' ? unread : t.key === tab ? (submissions ? submissions.length : null) : null;
          return {
            value: t.key,
            label: count === null ? t.label : `${t.label} (${count})`,
          };
        })}
        onChange={(next) => {
          setTab(next);
          setExpanded(null);
        }}
        className="max-w-full flex-wrap"
      />

      {submissions === null ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : submissions.length === 0 ? (
        <EmptyState
          title={EMPTY_COPY[tab].title}
          description={EMPTY_COPY[tab].description}
          action={
            tab === 'new' ? (
              siteId ? (
                <Link
                  to={locationRoutes.website(businessId, siteId)}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  See your website pages
                </Link>
              ) : null
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTab('new');
                  setExpanded(null);
                }}
              >
                Go to New
              </Button>
            )
          }
        />
      ) : (
        <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-card">
          {submissions.map((s) => {
            const open = expanded === s.id;
            const badge = STATUS_BADGE[s.status];
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => toggleExpand(s)}
                  className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">{s.dataJson.name}</span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                    <p className="truncate text-xs text-text-tertiary">{s.dataJson.email}</p>
                    <p className={cn('mt-0.5 text-sm text-muted-foreground', open ? '' : 'truncate')}>
                      {s.dataJson.message}
                    </p>
                  </div>
                  <span className="shrink-0 pt-0.5 text-xs text-text-tertiary">
                    {relativeTime(s.submittedAt)}
                  </span>
                </button>

                {open ? (
                  <div className="flex flex-col gap-4 border-t border-border-subtle px-4 py-4">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {s.dataJson.message}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <a
                        href={`mailto:${s.dataJson.email}`}
                        className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline"
                      >
                        {s.dataJson.email}
                      </a>
                      <div className="flex flex-wrap gap-2">
                        {s.status !== 'archived' ? (
                          <Button type="button" variant="outline" size="sm" onClick={() => setStatus(s, 'archived')}>
                            <Archive className="h-4 w-4" />
                            Archive
                          </Button>
                        ) : null}
                        {s.status !== 'spam' ? (
                          <Button type="button" variant="outline" size="sm" onClick={() => setStatus(s, 'spam')}>
                            <Ban className="h-4 w-4" />
                            Mark spam
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="hover:bg-danger-bg hover:text-danger-fg"
                          onClick={() => remove(s)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </PageContainer>
  );
}

export default Inbox;
